import {
  createDateTimeFormatter,
  DEFAULT_DATE_TIME_FALLBACK,
  type DateTimeFormatter,
} from './index'

export type DateInput = string | Date | null | undefined

export type DateTimeOptions = {
  locale: string
  timeZone: string
  withSeconds?: boolean
  fallback?: string
}

export type CalendarDateTimeOptions = Omit<DateTimeOptions, 'timeZone'> & {timeZone?: string}

export type CalendarDateTimeFormatter = Pick<DateTimeFormatter,
  | 'locale' | 'fallback'
  | 'formatLocalDateWithOptions' | 'formatLocalDateRangeWithOptions'
  | 'formatLocalDate' | 'formatLocalDateWithWeekday' | 'formatLocalDateDay'
  | 'formatLocalDateWeekday' | 'formatLocalDateRange' | 'formatLocalDateRangeWithWeekday'
  | 'formatLocalTime' | 'formatLocalDateTime' | 'formatDayBoundaryTime'
  | 'formatTimeRange' | 'formatDayBoundaryTimeRange' | 'formatSession'
  | 'formatWeekday' | 'formatWeekdayRange' | 'formatWeekdayTime'
  | 'formatMonthTitle' | 'formatLocalDateMonthTitle'
>

export type AuditDateTimeOptions = Omit<DateTimeOptions, 'withSeconds'>

export type SessionRangeOptions = DateTimeOptions & {
  dateStyle?: 'compact' | 'long'
  includeYear?: boolean
}

export type SessionRangeLabel = {
  timeLabel: string
  dateLabel: string
  combinedLabel: string
  isCrossDay: boolean
}

export type EventSessionDisplay = Readonly<{
  primaryLabel: string
  secondaryLabel: string
  secondaryPrefix: string
  isCrossDay: boolean
}>

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u
const OFFSET_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/u

const formatterFor = (options: DateTimeOptions) =>
  createDateTimeFormatter({
    locale: options.locale,
    timeZone: options.timeZone,
    fallback: options.fallback ?? DEFAULT_DATE_TIME_FALLBACK,
  })

export const dateTimeForLocale = (
  locale: string,
  timeZone: string,
) => formatterFor({locale, timeZone})

/** Calendar-only values have no zone. UTC is internal to their Intl representation. */
export const calendarDateTimeForLocale = (locale: string): CalendarDateTimeFormatter =>
  formatterFor({locale, timeZone: 'UTC'})

const asInstant = (value: DateInput): Date | string | null => {
  if (!value) return null
  if (value instanceof Date) return value
  const normalized = value.trim()
  return normalized.includes('T') && OFFSET_PATTERN.test(normalized) ? normalized : null
}

const asLocalDate = (
  value: DateInput,
  options: CalendarDateTimeOptions,
): string | null => {
  if (typeof value === 'string' && LOCAL_DATE_PATTERN.test(value.trim())) {
    return value.trim()
  }
  const instant = asInstant(value)
  if (!instant) return null
  if (!options.timeZone) throw new RangeError('An explicit time zone is required for an instant')
  return formatterFor({...options, timeZone: options.timeZone}).dateKeyInZone(instant)
}

export const formatDateLabel = (
  value: DateInput,
  options: CalendarDateTimeOptions,
): string => {
  const dateTime = formatterFor({...options, timeZone: 'UTC'})
  return dateTime.formatLocalDate(asLocalDate(value, options))
}

export const formatTimeLabel = (
  value: DateInput,
  options: DateTimeOptions,
): string =>
  formatterFor(options).formatInstantTime(asInstant(value), {
    seconds: options.withSeconds,
  })

export const formatDateTimeLabel = (
  value: DateInput,
  options: DateTimeOptions,
): string =>
  formatterFor(options).formatInstantDateTime(asInstant(value), {
    seconds: options.withSeconds,
  })

export const formatAuditDateTimeLabel = (
  value: DateInput,
  options: AuditDateTimeOptions,
): string => formatDateTimeLabel(value, {...options, withSeconds: true})

export const formatTimeRange = (
  start: DateInput,
  end: DateInput,
  options: DateTimeOptions,
): string => formatterFor(options).formatInstantTimeRange(asInstant(start), asInstant(end), {
  seconds: options.withSeconds,
})

export const formatSessionRange = (
  start: DateInput,
  end: DateInput,
  options: SessionRangeOptions,
): SessionRangeLabel => formatterFor(options).formatInstantSession({
  start: asInstant(start),
  end: asInstant(end),
  dateStyle: options.dateStyle === 'long' ? 'long' : 'numeric',
  includeYear: options.includeYear ?? true,
  seconds: options.withSeconds,
})

export const formatEventSessionDisplay = (
  dateTime: Pick<DateTimeFormatter, 'formatInstantDateTime' | 'formatInstantSession'>,
  start: DateInput,
  end: DateInput,
): EventSessionDisplay => {
  const session = dateTime.formatInstantSession({start, end})
  if (!session.isCrossDay) {
    return {
      primaryLabel: session.dateLabel,
      secondaryLabel: session.timeLabel,
      secondaryPrefix: '',
      isCrossDay: false,
    }
  }

  return {
    primaryLabel: dateTime.formatInstantDateTime(start),
    secondaryLabel: dateTime.formatInstantDateTime(end),
    secondaryPrefix: '-\u00a0',
    isCrossDay: true,
  }
}

export const formatDateRangeLabel = (
  start: DateInput,
  end: DateInput,
  options: CalendarDateTimeOptions,
): string => {
  const dateTime = formatterFor({...options, timeZone: 'UTC'})
  return dateTime.formatLocalDateRange(
    asLocalDate(start, options),
    asLocalDate(end, options),
  )
}

/** Activity details use the same compact weekday/date contract in every audience. */
export const formatActivitySession = (
  dateTime: Pick<DateTimeFormatter, 'dateKeyInZone' | 'formatInstantTime' | 'formatInstantTimeRange' | 'formatLocalDateWithWeekday'>,
  start: DateInput,
  end: DateInput,
): SessionRangeLabel => {
  const startDate = dateTime.dateKeyInZone(start)
  const endDate = dateTime.dateKeyInZone(end)
  const date = (key: string | null) => dateTime.formatLocalDateWithWeekday(key, {
    dateStyle: 'numeric', weekday: 'short', context: 'inline', includeYear: true,
  })
  if (startDate && endDate && startDate !== endDate) {
    const combinedLabel = `${date(startDate)} · ${dateTime.formatInstantTime(start)} – ${date(endDate)} · ${dateTime.formatInstantTime(end)}`
    return {dateLabel: '', timeLabel: combinedLabel, combinedLabel, isCrossDay: true}
  }
  const dateLabel = date(startDate)
  const timeLabel = dateTime.formatInstantTimeRange(start, end)
  // The hours lead, as in every other session label.
  return {dateLabel, timeLabel, combinedLabel: `${timeLabel} · ${dateLabel}`, isCrossDay: false}
}
